import { clamp } from 'lodash-es';

import { css, styled } from '@mui/material';

import Skeleton from '../Skeleton/Skeleton';
import { skeletonClasses } from '../Skeleton/theme';
import { pageHeaderSkeletonClasses } from './theme';

import type { PageHeaderSkeletonOwnerState, PageHeaderSkeletonProps } from './types';

export default function PageHeaderSkeleton(props: PageHeaderSkeletonProps) {
  const {
    breadcrumbsCount = 2,
    extraActionsCount = 5,
    showMainAction = false,
    showBreadcrumb = true,
    showDescription = false,
    showBottomDivider = false,
    showExtraActions = false,
    ...others
  } = props;

  const ownerState: PageHeaderSkeletonOwnerState = {
    ...props,
    breadcrumbsCount,
    extraActionsCount,
    showMainAction,
    showBreadcrumb,
    showDescription,
    showBottomDivider,
    showExtraActions,
  };

  const extraActions = Array(clamp(extraActionsCount, 1, 10)).fill(1);

  return (
    <PageHeaderSkeletonRoot {...others} ownerState={ownerState}>
      <PageHeaderSkeletonMainContainer ownerState={ownerState}>
        <PageHeaderSkeletonEssentials ownerState={ownerState}>
          {showBreadcrumb && (
            <PageHeaderSkeletonBreadcrumbs ownerState={ownerState}>
              {Array(clamp(breadcrumbsCount, 1, 5))
                .fill(1)
                .map((_, i) => (
                  <Skeleton key={i} shape="text" />
                ))}
            </PageHeaderSkeletonBreadcrumbs>
          )}
          <PageHeaderSkeletonTextContainer ownerState={ownerState}>
            <PageHeaderSkeletonTitle ownerState={ownerState}>
              <Skeleton shape="text" />
            </PageHeaderSkeletonTitle>
            {showDescription && (
              <PageHeaderSkeletonDescription ownerState={ownerState}>
                <Skeleton shape="text" />
              </PageHeaderSkeletonDescription>
            )}
          </PageHeaderSkeletonTextContainer>
        </PageHeaderSkeletonEssentials>
        {showMainAction && (
          <PageHeaderSkeletonMainAction ownerState={ownerState}>
            <Skeleton shape="rectangle" />
          </PageHeaderSkeletonMainAction>
        )}
      </PageHeaderSkeletonMainContainer>
      {showExtraActions && extraActionsCount > 0 && (
        <PageHeaderSkeletonExtraActions ownerState={ownerState}>
          {extraActions.map((_, i) => (
            <Skeleton key={i} shape="rectangle" />
          ))}
        </PageHeaderSkeletonExtraActions>
      )}
      {showBottomDivider && <PageHeaderSkeletonBottomDivider ownerState={ownerState} />}
    </PageHeaderSkeletonRoot>
  );
}

const PageHeaderSkeletonRoot = styled('div', {
  target: pageHeaderSkeletonClasses.root,
})<{ ownerState: PageHeaderSkeletonOwnerState }>(
  () => css`
    display: flex;
    flex-direction: column;
  `,
);

const PageHeaderSkeletonMainContainer = styled('div', {
  target: pageHeaderSkeletonClasses.mainContainer,
})<{ ownerState: PageHeaderSkeletonOwnerState }>(
  () => css`
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: nowrap;
  `,
);

const PageHeaderSkeletonEssentials = styled('div', {
  target: pageHeaderSkeletonClasses.essentials,
})<{ ownerState: PageHeaderSkeletonOwnerState }>(
  () => css`
    flex: 1 1 0;
    display: flex;
    flex-direction: column;
    gap: 8px;
  `,
);

const PageHeaderSkeletonBreadcrumbs = styled('div', {
  target: pageHeaderSkeletonClasses.breadcrumbs,
})<{ ownerState: PageHeaderSkeletonOwnerState }>(
  () => css`
    display: flex;
    align-items: center;
    gap: 5.5px;
    height: 24px;

    .${skeletonClasses.root} {
      width: 100px;
      height: 10px;
    }
  `,
);

const PageHeaderSkeletonTextContainer = styled('div', {
  target: pageHeaderSkeletonClasses.textContainer,
})<{ ownerState: PageHeaderSkeletonOwnerState }>(
  () => css`
    display: flex;
    flex-direction: column;
    gap: 6px;
  `,
);

const PageHeaderSkeletonTitle = styled('div', {
  target: pageHeaderSkeletonClasses.title,
})<{ ownerState: PageHeaderSkeletonOwnerState }>(
  () => css`
    .${skeletonClasses.root} {
      width: 320px;
      height: 36px;
    }
  `,
);

const PageHeaderSkeletonDescription = styled('div', {
  target: pageHeaderSkeletonClasses.description,
})<{ ownerState: PageHeaderSkeletonOwnerState }>(
  () => css`
    .${skeletonClasses.root} {
      width: 480px;
      height: 10px;
    }
  `,
);

const PageHeaderSkeletonMainAction = styled('div', {
  target: pageHeaderSkeletonClasses.mainAction,
})<{ ownerState: PageHeaderSkeletonOwnerState }>(
  () => css`
    .${skeletonClasses.root} {
      width: 83px;
      height: 40px;
    }
  `,
);

const PageHeaderSkeletonExtraActions = styled('div', {
  target: pageHeaderSkeletonClasses.extraActions,
})<{ ownerState: PageHeaderSkeletonOwnerState }>(
  () => css`
    display: flex;
    flex-direction: row;
    gap: 8px;
    margin-top: 16px;

    .${skeletonClasses.root} {
      width: 67px;
      height: 32px;
    }
  `,
);

const PageHeaderSkeletonBottomDivider = styled('div', {
  target: pageHeaderSkeletonClasses.bottomDivider,
})<{ ownerState: PageHeaderSkeletonOwnerState }>(
  ({ theme }) => css`
    margin-top: 16px;

    width: 100%;
    height: 1px;
    background-color: ${theme.color['border/default']};
  `,
);
